import React, { createContext, useContext } from "react";
import axios from "axios";
import { useToast } from "../context/ToastContext";

// API 핸들러 컨텍스트 생성
const ApiHandlerContext = createContext(null);

/**
 * API 핸들러 Provider 컴포넌트
 * 모든 API 응답 처리를 위한 공통 함수를 제공
 */
export const ApiHandlerProvider = ({ children }) => {
  const { toastSuccess, toastWarning, toastError } = useToast();

  // API 응답 처리 함수
  const handleApiResponse = async (apiPromise, options = {}) => {
    const {
      onSuccess,
      onError,
      showSuccessToast = false,
      successMessage = "요청이 성공적으로 처리되었습니다.",
    } = options;

    try {
      const response = await apiPromise;

      // API 응답 구조 확인
      if (!response || !response.data) {
        throw new Error("응답 데이터가 없습니다.");
      }

      // ApiResponseDTO 형식인지 확인
      if ("status" in response.data) {
        const { status, data, message } = response.data;

        // 성공 응답 처리
        if (status === "SUCCESS") {
          if (showSuccessToast) {
            toastSuccess(message || successMessage);
          }

          if (onSuccess && typeof onSuccess === "function") {
            onSuccess(data);
          }

          return data;
        }

        // 에러 상태 응답 처리
        if (status === "ERROR") {
          toastError(message || "처리 중 오류가 발생했습니다.");

          if (onError && typeof onError === "function") {
            onError(message);
          }

          const error = new Error(message || "처리 중 오류가 발생했습니다.");
          error.handled = true;
          throw error;
        }

        // 실패 상태 응답 처리 (유효성 검사 실패 등)
        if (status === "FAIL") {
          if (message) {
            // message 필드 우선 사용
            toastWarning(message);
          } else if (typeof data === "object" && data !== null) {
            // message가 없으면 필드별 에러 메시지에서 추출
            const firstErrorKey = Object.keys(data)[0];
            const firstErrorMessage = data[firstErrorKey];
            toastWarning(firstErrorMessage || "입력 정보를 확인해주세요.");
          } else {
            toastWarning("입력 정보를 확인해주세요.");
          }

          if (onError && typeof onError === "function") {
            onError(data || message);
          }

          const error = new Error("요청 처리에 실패했습니다.");
          error.handled = true;
          error.data = data;
          throw error;
        }
      } else {
        // 표준 형식이 아닌 경우 (직접 데이터 반환)
        if (showSuccessToast) {
          toastSuccess(successMessage);
        }

        if (onSuccess && typeof onSuccess === "function") {
          onSuccess(response.data);
        }

        return response.data;
      }
    } catch (error) {
      // 이미 처리된 에러는 다시 처리하지 않음
      if (error.handled) {
        throw error;
      }

      // axios 에러인 경우 추가 처리
      if (axios.isAxiosError(error)) {
        const { response } = error;

        if (response) {
          // HTTP 상태 코드별 처리
          switch (response.status) {
            case 400:
              toastWarning(response.data?.message || "잘못된 요청입니다.");
              break;
            case 401:
              toastError("인증이 필요합니다. 다시 로그인해주세요.");
              // 로그인 페이지로 리다이렉트하는 로직 추가 가능
              break;
            case 403:
              toastError("접근 권한이 없습니다.");
              break;
            case 404:
              toastWarning("요청한 리소스를 찾을 수 없습니다.");
              break;
            case 409:
              toastWarning(response.data?.message || "요청이 충돌했습니다.");
              break;
            case 500:
              toastError(
                "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
              );
              break;
            default:
              toastError("요청 처리 중 오류가 발생했습니다.");
          }
        } else {
          // 네트워크 오류 등
          toastError(
            "서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요."
          );
        }
      } else {
        // 기타 에러
        toastError(error.message || "예기치 않은 오류가 발생했습니다.");
      }

      if (onError && typeof onError === "function") {
        onError(error);
      }

      // 에러 처리 완료 표시
      error.handled = true;
      throw error;
    }
  };

  // 비표준 API 응답 처리 함수
  const handleNonStandardApiResponse = async (apiPromise, options = {}) => {
    const {
      onSuccess,
      onError,
      showSuccessToast = false,
      successMessage = "요청이 성공적으로 처리되었습니다.",
    } = options;

    try {
      const response = await apiPromise;

      // 성공 처리
      if (showSuccessToast) {
        toastSuccess(successMessage);
      }

      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(response.data);
      }

      return response.data;
    } catch (error) {
      // 에러 처리
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "요청 처리 중 오류가 발생했습니다.";
      toastError(errorMessage);

      if (onError && typeof onError === "function") {
        onError(error);
      }

      error.handled = true;
      throw error;
    }
  };

  const value = {
    handleApiResponse,
    handleNonStandardApiResponse,
  };

  return (
    <ApiHandlerContext.Provider value={value}>
      {children}
    </ApiHandlerContext.Provider>
  );
};

/**
 * API 핸들러 훅
 * API 응답 처리 함수들을 제공
 */
export const useApiHandler = () => {
  const context = useContext(ApiHandlerContext);
  if (!context) {
    throw new Error(
      "useApiHandler는 ApiHandlerProvider 내에서 사용해야 합니다."
    );
  }
  return context;
};

/**
 * HOC 버전 - 컨텍스트를 직접 사용하지 않고 싶은 경우
 * 단독 함수로 API 핸들러 제공
 */
export const withApiHandler = (Component) => {
  return (props) => {
    const { handleApiResponse, handleNonStandardApiResponse } = useApiHandler();
    return (
      <Component
        {...props}
        handleApiResponse={handleApiResponse}
        handleNonStandardApiResponse={handleNonStandardApiResponse}
      />
    );
  };
};

/**
 * 독립적인 API 핸들러 함수 - 컨텍스트를 사용하지 않는 대안
 * 커스텀 토스트 함수를 직접 받아서 사용
 */
export const createApiHandler = (toastFunctions) => {
  const { toastSuccess, toastWarning, toastError } = toastFunctions;

  return {
    handleApiResponse: async (apiPromise, options = {}) => {
      const {
        onSuccess,
        onError,
        showSuccessToast = false,
        successMessage = "요청이 성공적으로 처리되었습니다.",
      } = options;

      try {
        const response = await apiPromise;

        // API 응답 구조 확인
        if (!response || !response.data) {
          throw new Error("응답 데이터가 없습니다.");
        }

        // ApiResponseDTO 형식인지 확인
        if ("status" in response.data) {
          const { status, data, message } = response.data;

          // 성공 응답 처리
          if (status === "SUCCESS") {
            if (showSuccessToast) {
              toastSuccess(message || successMessage);
            }

            if (onSuccess && typeof onSuccess === "function") {
              onSuccess(data);
            }

            return data;
          }

          // 에러 상태 응답 처리
          if (status === "ERROR") {
            toastError(message || "처리 중 오류가 발생했습니다.");

            if (onError && typeof onError === "function") {
              onError(message);
            }

            const error = new Error(message || "처리 중 오류가 발생했습니다.");
            error.handled = true;
            throw error;
          }

          // 실패 상태 응답 처리 (유효성 검사 실패 등)
          if (status === "FAIL") {
            if (message) {
              // message 필드 우선 사용
              toastWarning(message);
            } else if (typeof data === "object" && data !== null) {
              // message가 없으면 필드별 에러 메시지에서 추출
              const firstErrorKey = Object.keys(data)[0];
              const firstErrorMessage = data[firstErrorKey];
              toastWarning(firstErrorMessage || "입력 정보를 확인해주세요.");
            } else {
              toastWarning("입력 정보를 확인해주세요.");
            }

            if (onError && typeof onError === "function") {
              onError(data || message);
            }

            const error = new Error("요청 처리에 실패했습니다.");
            error.handled = true;
            error.data = data;
            throw error;
          }
        } else {
          // 표준 형식이 아닌 경우 (직접 데이터 반환)
          if (showSuccessToast) {
            toastSuccess(successMessage);
          }

          if (onSuccess && typeof onSuccess === "function") {
            onSuccess(response.data);
          }

          return response.data;
        }
      } catch (error) {
        // 이미 처리된 에러는 다시 처리하지 않음
        if (error.handled) {
          throw error;
        }

        // axios 에러인 경우 추가 처리
        if (axios.isAxiosError(error)) {
          const { response } = error;

          if (response) {
            // HTTP 상태 코드별 처리
            switch (response.status) {
              case 400:
                toastWarning(response.data?.message || "잘못된 요청입니다.");
                break;
              case 401:
                toastError("인증이 필요합니다. 다시 로그인해주세요.");
                break;
              case 403:
                toastError("접근 권한이 없습니다.");
                break;
              case 404:
                toastWarning("요청한 리소스를 찾을 수 없습니다.");
                break;
              case 409:
                toastWarning(response.data?.message || "요청이 충돌했습니다.");
                break;
              case 500:
                toastError(
                  "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
                );
                break;
              default:
                toastError("요청 처리 중 오류가 발생했습니다.");
            }
          } else {
            // 네트워크 오류 등
            toastError(
              "서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요."
            );
          }
        } else {
          // 기타 에러
          toastError(error.message || "예기치 않은 오류가 발생했습니다.");
        }

        if (onError && typeof onError === "function") {
          onError(error);
        }

        // 에러 처리 완료 표시
        error.handled = true;
        throw error;
      }
    },

    handleNonStandardApiResponse: async (apiPromise, options = {}) => {
      const {
        onSuccess,
        onError,
        showSuccessToast = false,
        successMessage = "요청이 성공적으로 처리되었습니다.",
      } = options;

      try {
        const response = await apiPromise;

        // 성공 처리
        if (showSuccessToast) {
          toastSuccess(successMessage);
        }

        if (onSuccess && typeof onSuccess === "function") {
          onSuccess(response.data);
        }

        return response.data;
      } catch (error) {
        // 에러 처리
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "요청 처리 중 오류가 발생했습니다.";
        toastError(errorMessage);

        if (onError && typeof onError === "function") {
          onError(error);
        }

        error.handled = true;
        throw error;
      }
    },
  };
};
